import React from "react";
import { useModal } from "context";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import FormButton from "reusableComponents/FormButton";
import AddNewBookingModal from "modals/AddNewBookingModal";
import ReusableConfirmationModal from "modals/ReusableConfirmationModal";
import createPrivateBookingService from "services/CreatePrivateBookingService";
import {
  CopyIcon,
  ExternalLinkIcon,
  CalendarIcon,
  UserIcon,
  MapPinIcon,
  UsersIcon,
  EditIcon,
  DeleteIcon,
} from "../GoogleEventModal/svgIcons";

import styles from "./styles.module.scss";

const formatDateForDisplay = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return isNaN(d.getTime())
      ? ""
      : d.toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
  } catch {
    return "";
  }
};

const formatTimeForDisplay = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

// Helper to format date to YYYY-MM-DD
const formatDateToYYYYMMDD = (date) => {
  if (!date) return null;
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return null;
  }
};

// Helper to format time to HH:MM
const formatTimeToHHMM = (date) => {
  if (!date) return null;
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch {
    return null;
  }
};

const PrivateBookingDetailsModal = ({
  event = null,
  onClose = () => {},
  onDeleteSuccess = () => {},
  onSubmit = () => {},
}) => {
  const { t } = useTranslation("global");
  const { openModal, closeModal } = useModal();
  const token = useSelector((state) => state.auth.token);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isDuplicating, setIsDuplicating] = React.useState(false);

  const privateBookingService = React.useMemo(
    () => createPrivateBookingService(token),
    [token]
  );

  if (!event) {
    return (
      <div className={styles.modal}>
        <div className={styles.errorMessage}>
          {t("modal.providerBookingDetails.noBookingData")}
        </div>
        <div className={styles.footer}>
          <FormButton
            label={t("buttons.close")}
            onClick={onClose}
            variant="secondary"
          />
        </div>
      </div>
    );
  }

  // DEBUG: Log the full event object
  console.log('[PrivateBookingDetailsModal] 🔍 Full event object:', event);
  console.log('[PrivateBookingDetailsModal] 🔍 event.extendedProps:', event.extendedProps);
  console.log('[PrivateBookingDetailsModal] 🔍 event.extendedProps.place:', event.extendedProps?.place);
  console.log('[PrivateBookingDetailsModal] 🔍 event.extendedProps.placeGroup:', event.extendedProps?.placeGroup);
  console.log('[PrivateBookingDetailsModal] 🔍 event.extendedProps.staff:', event.extendedProps?.staff);

  const bookingData = {
    id: event?.id || "",
    title: event?.title || t("modal.providerBookingDetails.noTitle"),
    place: event?.extendedProps?.place || null,
    staff: event?.extendedProps?.staff || null,
    startDate: event?.start,
    endDate: event?.end,
    capacity: event?.extendedProps?.capacity || 0,
    customer: {
      firstName: event?.extendedProps?.customer?.firstName || "N/A",
      lastName: event?.extendedProps?.customer?.lastName || "N/A",
      email: event?.extendedProps?.customer?.email || "N/A",
      phone: event?.extendedProps?.customer?.phone || "N/A",
      company: event?.extendedProps?.customer?.company || "N/A",
    },
    notes: event?.extendedProps?.services_equipments || "",
    services_equipments: event?.extendedProps?.services_equipments || "",
    disposition_type: event?.extendedProps?.disposition_type || "",
  };

  console.log('[PrivateBookingDetailsModal] 📦 bookingData extracted:', bookingData);

  // Helper function to format booking for AddNewBookingModal
  const formatBookingForModal = (booking, isDuplicate = false) => {
    console.log('[PrivateBookingDetailsModal] 🔧 formatBookingForModal called with:', booking);
    console.log('[PrivateBookingDetailsModal] 🔧 booking.place:', booking.place);
    console.log('[PrivateBookingDetailsModal] 🔧 booking.staff:', booking.staff);
    
    // Get the place object with proper structure for SelectInput
    let place = null;
    if (booking.place) {
      // Ensure place has all required fields for SelectInput
      place = {
        id: booking.place.id,
        name: booking.place.name || booking.place.label || "Unknown",
        label: booking.place.name || booking.place.label || "Unknown",
        value: booking.place.id,
        placeUsage: booking.place.placeUsage || null,
        minimal_duration: booking.place.minimal_duration || null,
        placeGroup: booking.place.placeGroup || null,
        // Preserve all original data
        ...booking.place
      };
      
      // Ensure placeGroup is properly structured if it exists
      if (place.placeGroup && typeof place.placeGroup === 'object') {
        place.placeGroup = {
          id: place.placeGroup.id,
          label: place.placeGroup.label || place.placeGroup.name || "Group",
          value: place.placeGroup.id,
          ...place.placeGroup
        };
      }
      console.log('[PrivateBookingDetailsModal] ✅ Formatted place:', place);
    } else {
      console.log('[PrivateBookingDetailsModal] ⚠️ No place found in booking');
    }
    
    // Get staff with proper structure for SelectInput
    let staff = null;
    if (booking.staff) {
      staff = {
        id: booking.staff.id,
        name: booking.staff.name || booking.staff.label || "Unknown",
        label: booking.staff.name || booking.staff.label || "Unknown",
        value: booking.staff.id,
        ...booking.staff
      };
      console.log('[PrivateBookingDetailsModal] ✅ Formatted staff:', staff);
    } else {
      console.log('[PrivateBookingDetailsModal] ⚠️ No staff found in booking');
    }
    
    // Get group filter with proper structure for SelectInput
    let groupFilter = null;
    if (place?.placeGroup) {
      groupFilter = {
        id: place.placeGroup.id,
        label: place.placeGroup.label || place.placeGroup.name || "Group",
        value: place.placeGroup.id,
        ...place.placeGroup
      };
      console.log('[PrivateBookingDetailsModal] ✅ Formatted groupFilter:', groupFilter);
    } else {
      console.log('[PrivateBookingDetailsModal] ⚠️ No groupFilter found');
    }
    
    // Handle date objects - ensure they're valid
    const startDateObj = booking.startDate instanceof Date ? booking.startDate : new Date(booking.startDate);
    const endDateObj = booking.endDate instanceof Date ? booking.endDate : new Date(booking.endDate);
    
    const startDateStr = formatDateToYYYYMMDD(startDateObj);
    const endDateStr = formatDateToYYYYMMDD(endDateObj);
    const startHour = formatTimeToHHMM(startDateObj);
    const endHour = formatTimeToHHMM(endDateObj);

    // Build the formatted booking object
    const formatted = {
      // Basic info
      id: isDuplicate ? undefined : booking.id,
      title: booking.title || "",
      
      // Place, staff, and group filter with proper SelectInput structure
      place: place,
      staff: staff,
      groupFilter: groupFilter,
      
      // Date and time fields (as strings for the modal)
      start_date: startDateStr,
      end_date: endDateStr,
      start_hour: startHour || "09:00",
      end_hour: endHour || "17:00",
      
      // Customer info
      customer_social_reason: booking.customer?.company || "",
      customer_first_name: booking.customer?.firstName || "",
      customer_last_name: booking.customer?.lastName || "",
      customer_email: booking.customer?.email || "",
      customer_phone: booking.customer?.phone || "",
      
      // Additional fields
      services_equipments: booking.services_equipments || booking.notes || "",
      capacity: booking.capacity || 1,
      disposition_type: booking.disposition_type || "",
      
      // Date objects for the modal
      start_date_dt: startDateObj,
      end_date_dt: endDateObj,
      start_hour_dt: startHour || "09:00",
      end_hour_dt: endHour || "17:00",
    };

    // If duplicating, remove the id
    if (isDuplicate) {
      delete formatted.id;
    }

    console.log('[PrivateBookingDetailsModal] 📤 FINAL formatted booking:', formatted);
    console.log('[PrivateBookingDetailsModal] 📤 formatted.place:', formatted.place);
    console.log('[PrivateBookingDetailsModal] 📤 formatted.staff:', formatted.staff);
    console.log('[PrivateBookingDetailsModal] 📤 formatted.groupFilter:', formatted.groupFilter);
    return formatted;
  };

  const handleEdit = () => {
    console.log('[PrivateBookingDetailsModal] ✏️ handleEdit called');
    const formattedBooking = formatBookingForModal(bookingData, false);
    console.log('[PrivateBookingDetailsModal] 📤 Opening edit modal with:', formattedBooking);
    
    openModal({
      size: "medium",
      title: t("modal.providerBookingDetails.editTitle"),
      content: (
        <AddNewBookingModal
          initialBooking={formattedBooking}
          readonly={false}
          onClose={closeModal}
          onSubmit={onSubmit}
        />
      ),
    });
  };

  const handleDuplicate = () => {
    setIsDuplicating(true);
    
    const duplicatedBooking = formatBookingForModal(bookingData, true);
    
    openModal({
      size: "medium",
      title: t("modal.providerBookingDetails.duplicateTitle"),
      content: (
        <AddNewBookingModal
          initialBooking={duplicatedBooking}
          readonly={false}
          onClose={() => {
            setIsDuplicating(false);
            closeModal();
          }}
          onSubmit={async (bookingData, sendMail) => {
            try {
              const result = await onSubmit(bookingData, sendMail);
              if (result?.success) {
                setIsDuplicating(false);
                closeModal();
              }
              return result;
            } catch (error) {
              console.error("Error duplicating booking:", error);
              throw error;
            }
          }}
        />
      ),
    });
  };

  const handleDelete = () => {
    openModal({
      content: (
        <ReusableConfirmationModal
          message={t("modal.providerBookingDetails.deleteConfirm")}
          onConfirm={() => executeDelete(bookingData.id)}
          confirmText={t("buttons.delete")}
          cancelText={t("buttons.cancel")}
        />
      ),
      title: t("modal.providerBookingDetails.deleteTitle"),
    });
  };

  const executeDelete = async (bookingId) => {
    setIsDeleting(true);
    try {
      const result = await privateBookingService.deletePrivateBooking(
        bookingId
      );
      if (!result.success)
        throw new Error(result.error?.message || t("errors.deleteFailed"));

      setTimeout(() => {
        openModal({
          content: <p>{result.message}</p>,
          type: "success",
          autoClose: true,
          closeDelay: 1500,
        });
      }, 400);

      onDeleteSuccess();
      return true;
    } catch (error) {
      console.error("Delete error:", error);
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopyDetails = () => {
    const startDateFormatted = formatDateForDisplay(bookingData.startDate);
    const endDateFormatted = formatDateForDisplay(bookingData.endDate);
    
    const details = [
      `${t("modal.providerBookingDetails.eventLabel")}: ${bookingData.title}`,
      `${t("modal.providerBookingDetails.startDateLabel")}: ${startDateFormatted}`,
      `${t("modal.providerBookingDetails.endDateLabel")}: ${endDateFormatted}`,
      `${t("modal.providerBookingDetails.startTimeLabel")}: ${formatTimeForDisplay(bookingData.startDate)}`,
      `${t("modal.providerBookingDetails.endTimeLabel")}: ${formatTimeForDisplay(bookingData.endDate)}`,
      `${t("modal.providerBookingDetails.locationLabel")}: ${bookingData.place?.name || "N/A"}`,
      `${t("modal.providerBookingDetails.customerLabel")}: ${bookingData.customer.firstName} ${bookingData.customer.lastName}`,
      `${t("modal.providerBookingDetails.companyLabel")}: ${bookingData.customer.company}`,
      `${t("modal.providerBookingDetails.emailLabel")}: ${bookingData.customer.email}`,
      `${t("modal.providerBookingDetails.phoneLabel")}: ${bookingData.customer.phone}`,
      `${t("modal.providerBookingDetails.capacityLabel")}: ${bookingData.capacity} ${t("modal.providerBookingDetails.peopleLabel")}`,
      `${t("modal.providerBookingDetails.notesLabel")}: ${bookingData.notes || "N/A"}`,
    ]
      .filter(Boolean)
      .join("\n");

    navigator.clipboard
      .writeText(details)
      .then(() => alert(t("modal.providerBookingDetails.copySuccess")))
      .catch(() => alert(t("modal.providerBookingDetails.copyFailed")));
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.eventTypeBadge}>
            <span className={styles.bookingLogo}>B</span>
            {t("modal.providerBookingDetails.badgeLabel")}
          </div>
          <h2 className={styles.title}>{bookingData.title}</h2>
          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <div className={styles.content}>
          {/* Date and Time Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <CalendarIcon className={styles.sectionIcon} />
              {t("modal.providerBookingDetails.dateTimeSection")}
            </h3>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t("modal.providerBookingDetails.startDateLabel")}
                </span>
                <span className={styles.detailValue}>
                  {formatDateForDisplay(bookingData.startDate)}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t("modal.providerBookingDetails.endDateLabel")}
                </span>
                <span className={styles.detailValue}>
                  {formatDateForDisplay(bookingData.endDate)}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t("modal.providerBookingDetails.startTimeLabel")}
                </span>
                <span className={styles.detailValue}>
                  {formatTimeForDisplay(bookingData.startDate)}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t("modal.providerBookingDetails.endTimeLabel")}
                </span>
                <span className={styles.detailValue}>
                  {formatTimeForDisplay(bookingData.endDate)}
                </span>
              </div>
            </div>
          </div>

          {/* Location Section */}
          {bookingData.place && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <MapPinIcon className={styles.sectionIcon} />
                {t("modal.providerBookingDetails.locationSection")}
              </h3>
              <div className={styles.locationCard}>
                <div className={styles.locationName}>
                  {bookingData.place.name}
                </div>
                {bookingData.place.address && (
                  <div className={styles.locationAddress}>
                    {bookingData.place.address.address}
                    {bookingData.place.address.city &&
                      `, ${bookingData.place.address.city}`}
                    {bookingData.place.address.country &&
                      `, ${bookingData.place.address.country}`}
                  </div>
                )}
                <button
                  className={styles.mapLink}
                  onClick={() =>
                    window.open(
                      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        bookingData.place.name
                      )}`,
                      "_blank"
                    )
                  }
                >
                  <ExternalLinkIcon className={styles.linkIcon} />
                  {t("modal.providerBookingDetails.viewOnMap")}
                </button>
              </div>
            </div>
          )}

          {/* Customer Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <UserIcon className={styles.sectionIcon} />
              {t("modal.providerBookingDetails.customerSection")}
            </h3>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t("modal.providerBookingDetails.nameLabel")}
                </span>
                <span className={styles.detailValue}>
                  {bookingData.customer.firstName}{" "}
                  {bookingData.customer.lastName}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t("modal.providerBookingDetails.companyLabel")}
                </span>
                <span className={styles.detailValue}>
                  {bookingData.customer.company}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t("modal.providerBookingDetails.emailLabel")}
                </span>
                <span className={styles.detailValue}>
                  <a href={`mailto:${bookingData.customer.email}`}>
                    {bookingData.customer.email}
                  </a>
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t("modal.providerBookingDetails.phoneLabel")}
                </span>
                <span className={styles.detailValue}>
                  <a href={`tel:${bookingData.customer.phone}`}>
                    {bookingData.customer.phone}
                  </a>
                </span>
              </div>
            </div>
          </div>

          {/* Capacity */}
          {bookingData.capacity > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <UsersIcon className={styles.sectionIcon} />
                {t("modal.providerBookingDetails.capacitySection")}
              </h3>
              <div className={styles.capacityBadge}>
                <span className={styles.capacityNumber}>
                  {bookingData.capacity}
                </span>
                <span className={styles.capacityLabel}>
                  {t("modal.providerBookingDetails.peopleLabel")}
                </span>
              </div>
            </div>
          )}

          {/* Notes */}
          {bookingData.notes && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <CalendarIcon className={styles.sectionIcon} />
                {t("modal.providerBookingDetails.notesSection")}
              </h3>
              <div className={styles.notes}>{bookingData.notes}</div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button
            className={styles.secondaryButton}
            onClick={handleCopyDetails}
          >
            <CopyIcon className={styles.buttonIcon} />
            {t("modal.providerBookingDetails.copyButton")}
          </button>
          
          <button
            className={styles.duplicateButton}
            onClick={handleDuplicate}
            disabled={isDuplicating}
          >
            <CopyIcon className={styles.buttonIcon} />
            {isDuplicating ? t("modal.providerBookingDetails.duplicating") : t("modal.providerBookingDetails.duplicateButton")}
          </button>
          
          <div className={styles.actionButtons}>
            <button
              className={styles.deleteButton}
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <DeleteIcon className={styles.buttonIcon} />
              {isDeleting ? t("modal.providerBookingDetails.deleting") : t("buttons.delete")}
            </button>
            <button className={styles.primaryButton} onClick={handleEdit}>
              <EditIcon className={styles.buttonIcon} />
              {t("buttons.edit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivateBookingDetailsModal;